import { IGetCurrentWeatherItem } from "app/shared/weather.service";
import { Address } from "./address";
import { Org } from "./org";
import { UserGroup } from "./user-group";
import { WeatherItem } from "./weather-item";

export class Site {
	id: number;
	title: string;
	isActive: boolean = true;
	isDeployable: boolean;
	addressId: number;
	addressTitle: string;
	address1: string;
	address2: string;
	address3: string;
	addressTown: string;
	addressCounty: string;
	addressCountry: string;
	addressPostcode: string;
	addressTel: string;
	addressEmail: string;
	addressLong: number;
	addressLat: number;
	address: Address;
	org: Org;
	localWeatherTemp: number;
	localWeatherIcon: string;
	localWeatherTitle: string;
	localWeatherLat: number;
	localWeatherLong: number;
	userGroups: UserGroup[] = [];
	createdAt: Date;
	regionId: number;
	regionTitle: string;
	gatewayCount: number;
	floorplanCount: number;
	shareCount: number; // Orgs shared with
	rags: any[] = [];
	floorplans: any[] = [];
	isFav: boolean;
	rag: string;
	settings: any = {};
	metobsid: number;
	weather: WeatherItem;
	weatherCurrent: IGetCurrentWeatherItem;
	checked: boolean;
	hasDashboard: boolean;

	// Admin

	notes: string;

	constructor(data?: any) {
		this.address = new Address();
		this.org = new Org();
		if (typeof data === 'number') {
			this.id = data;
		} else if (typeof data === 'object') {
			if (data.site) {
				this.userGroups = data.usergroups.map(userGroup => new UserGroup(userGroup));
				data = data.site;
			}
			this.address = new Address(data);
			this.addressId = data.addressId;
			this.addressTitle = data.addressTitle;
			this.address1 = data.address1;
			this.address2 = data.address2;
			this.address3 = data.address3;
			this.addressCounty = data.addressCounty;
			this.addressCountry = data.addressCountry;
			this.addressTown = data.addressTown;
			this.addressPostcode = data.addressPostcode;
			this.addressLat = data.addressLat;
			this.addressLong = data.addressLong;
			this.addressEmail = data.addressEmail;
			this.title = data.title;
			this.id = data.id;
			this.org = new Org({ id: data.org_id || data.orgId, title: data.orgTitle, shortTitle: data.orgShortTitle });
			this.isActive = data.isActive === true || data.isActive === 1;
			this.isDeployable = data.isDeployable === 1;
			this.localWeatherTemp = data.localWeatherTemp ?? '';
			this.localWeatherIcon = data.localWeatherIcon || '';
			this.localWeatherTitle = data.localWeatherTitle || '';
			this.localWeatherLat = data.localWeatherLat ? +data.localWeatherLat : null;
			this.localWeatherLong = data.localWeatherLong ? +data.localWeatherLong : null;

			this.createdAt = data.createdAt ? new Date(data.createdAt) : null;
			this.regionId = data.region_id;
			this.regionTitle = data.regionTitle || '';
			this.gatewayCount = data.gatewayCount ?? null;
			this.floorplanCount = data.floorplanCount ?? null;
			this.shareCount = data.shareCount ?? null;
			this.rag = data.siteRag;
			this.settings.floorplan_dashboard = data.floorplan_dashboard;
			this.metobsid = data.metobsId;
			this.hasDashboard = this.settings.floorplan_dashboard;
			this.notes = data.notes || '';
		}
	}

	addressDisplayForLine() {
		return this.address.displayForLine(this.title);
	}

	serialise() {


		return {
			id: this.id,
			title: this.title,
			orgId: this.org.id,
			addressTitle: this.address.addressTitle,
			address1: this.address.address1,
			address2: this.address.address2,
			address3: this.address.address3,
			addressTown: this.address.addressTown,
			addressCounty: this.address.addressCounty,
			addressCountry: this.address.addressCountry,
			addressPostcode: this.address.addressPostcode,
			addressTel: this.address.addressTel,
			addressEmail: this.address.addressEmail,
			addressLong: this.address.addressLong,
			addressLat: this.address.addressLat,
			regionId: this.regionId,
			metobsid: this.metobsid,
			notes: this.notes
		};
	}

	clone(): Site {
		const cloned = new Site();

		// Primitives
		cloned.id = this.id;
		cloned.title = this.title;
		cloned.isActive = this.isActive;
		cloned.isDeployable = this.isDeployable;
		cloned.addressTitle = this.addressTitle;
		cloned.addressId = this.addressId;
		cloned.address1 = this.address1;
		cloned.address2 = this.address2;
		cloned.address3 = this.address3;
		cloned.addressTown = this.addressTown;
		cloned.addressCounty = this.addressCounty;
		cloned.addressCountry = this.addressCountry;
		cloned.addressPostcode = this.addressPostcode;
		cloned.addressTel = this.addressTel;
		cloned.addressEmail = this.addressEmail;
		cloned.addressLong = this.addressLong;
		cloned.addressLat = this.addressLat;
		cloned.localWeatherTemp = this.localWeatherTemp;
		cloned.localWeatherIcon = this.localWeatherIcon;
		cloned.localWeatherTitle = this.localWeatherTitle;
		cloned.localWeatherLat = this.localWeatherLat;
		cloned.localWeatherLong = this.localWeatherLong;
		cloned.regionId = this.regionId;
		cloned.regionTitle = this.regionTitle;
		cloned.gatewayCount = this.gatewayCount;
		cloned.floorplanCount = this.floorplanCount;
		cloned.shareCount = this.shareCount;
		cloned.rag = this.rag;
		cloned.metobsid = this.metobsid;
		cloned.hasDashboard = this.hasDashboard;
		cloned.notes = this.notes;
		cloned.checked = this.checked;
		cloned.isFav = this.isFav;

		// Deep clone class instances
		cloned.address = this.address ? this.address.clone() : new Address();
		cloned.org = this.org ? this.org.clone() : new Org();
		cloned.userGroups = this.userGroups ? this.userGroups.map(ug => ug.clone()) : [];
		cloned.weather = this.weather ? new WeatherItem(this.weather) : null;
		cloned.weatherCurrent = this.weatherCurrent ? structuredClone(this.weatherCurrent) : null;
		cloned.settings = structuredClone(this.settings);
		cloned.rags = structuredClone(this.rags);
		cloned.floorplans = structuredClone(this.floorplans);
		cloned.createdAt = this.createdAt ? new Date(this.createdAt) : null;

		return cloned;
	}
}
